import { useEffect } from "react";
import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router-dom";
import styles from "./RouteErrorBoundary.module.scss";

/**
 * RouteErrorBoundary
 * ------------------
 * Used as the `errorElement` on React Router routes.
 *
 * - If the error is a "Failed to fetch dynamically imported module" (chunk-not-found
 *   after a new deployment), it automatically reloads the page ONCE so the browser
 *   picks up the new asset hashes.
 * - For all other errors it renders a clean, on-brand recovery screen.
 */
const RELOAD_KEY = "chunkErrorReloaded";

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  const isChunkError =
    error instanceof TypeError &&
    (error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.message.includes("error loading dynamically imported module"));

  useEffect(() => {
    if (isChunkError) {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
      if (!alreadyReloaded) {
        sessionStorage.setItem(RELOAD_KEY, "true");
        // Hard-reload to bypass the stale service-worker / CDN cache
        window.location.reload();
      }
    } else {
      // Clear the flag so future chunk errors can trigger a reload again
      sessionStorage.removeItem(RELOAD_KEY);
    }
  }, [isChunkError]);

  // While the reload is in flight just show nothing
  if (isChunkError && !sessionStorage.getItem(RELOAD_KEY)) return null;

  // ─── Friendly error UI ───────────────────────────────────────────────────────
  let statusCode = null;
  let message = "Something went wrong";
  let detail =
    "We ran into an unexpected issue. Don't worry – your data is safe. Try going back or refreshing the page.";

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    message =
      error.status === 404
        ? "Page not found"
        : error.status === 403
        ? "Access denied"
        : "Request failed";
    detail = error.data?.message || error.statusText || detail;
  } else if (isChunkError) {
    message = "App updated — reloading…";
    detail =
      "A new version of BizAura was deployed. Refreshing to load the latest assets.";
  } else if (error instanceof Error) {
    message = error.name || "Application Error";
    detail = error.message || detail;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        {/* Icon */}
        <div className={styles.iconRing}>
          <svg viewBox="0 0 24 24" fill="none" className={styles.icon}>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M12 8v4M12 16h.01"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {statusCode && <p className={styles.status}>{statusCode}</p>}
        <h1 className={styles.title}>{message}</h1>
        <p className={styles.detail}>{detail}</p>

        <div className={styles.actions}>
          <button
            id="error-go-back-btn"
            className={styles.btnSecondary}
            onClick={() => navigate(-1)}
          >
            ← Go back
          </button>
          <button
            id="error-go-home-btn"
            className={styles.btnPrimary}
            onClick={() => navigate("/", { replace: true })}
          >
            Go to Home
          </button>
          <button
            id="error-reload-btn"
            className={styles.btnGhost}
            onClick={() => window.location.reload()}
          >
            ↺ Reload page
          </button>
        </div>
      </div>
    </div>
  );
}
